import { findAllDutyRequests, getDashboardStats, getInquiries, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
  TitleContainer,
  Tooltip,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ko";
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  FileText,
  MessageSquare,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UserCheck,
  Users,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AnalyticsReportLinks } from "../analytics/AnalyticsReports";

dayjs.extend(relativeTime);
dayjs.locale("ko");

interface StatCardProps {
  title: string;
  value: number | string;
  unit?: string;
  icon: React.ReactNode;
  color: string;
  subtitle?: React.ReactNode;
}

function StatCard({ title, value, unit, icon, color, subtitle }: StatCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" fw={700} style={{ textTransform: "uppercase" }}>
            {title}
          </Text>
          <Group align="baseline" gap={4} mt={4}>
            <Title order={2} c={`${color}.7`}>
              {typeof value === "number" ? value.toLocaleString() : value}
            </Title>
            {unit && (
              <Text size="sm" fw={600} c="dimmed">
                {unit}
              </Text>
            )}
          </Group>
        </Stack>
        <Paper
          w={42}
          h={42}
          radius="md"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `var(--mantine-color-${color}-light)`,
            color: `var(--mantine-color-${color}-filled)`,
          }}
        >
          {icon}
        </Paper>
      </Group>

      {subtitle && <BoxSubtitle>{subtitle}</BoxSubtitle>}
    </Card>
  );
}

function BoxSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <Group gap="xs" mt="xs">
      {children}
    </Group>
  );
}

const SERVICE_TABS = ["hospital", "mini"] as const;
type ServiceTab = (typeof SERVICE_TABS)[number];

function isServiceTab(value: string | null): value is ServiceTab {
  return SERVICE_TABS.includes(value as ServiceTab);
}

export function HomePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: ServiceTab = isServiceTab(tabParam) ? tabParam : "hospital";

  const handleTabChange = (value: string | null) => {
    if (!isServiceTab(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };

  // 대시보드 요약 통계
  const {
    data: stats,
    isLoading: isStatsLoading,
    isFetching: isStatsFetching,
    refetch: refetchStats,
  } = useQuery({
    queryKey: queryKeys.dashboard.statistics,
    queryFn: () => getDashboardStats(),
  });

  // 대기 중인 근무 요청 목록 (최신 5건)
  const { data: dutyRequestsData, isLoading: isDutyRequestsLoading } = useQuery({
    queryKey: queryKeys.dutyRequests.list({
      page: 1,
      limit: 5,
      status: "PENDING",
    }),
    queryFn: () => findAllDutyRequests({ page: 1, limit: 5, status: "PENDING" }),
  });

  // 미답변 문의사항 목록 (최신 5건)
  const { data: inquiriesData, isLoading: isInquiriesLoading } = useQuery({
    queryKey: queryKeys.inquiries.adminList(1, 5, "OPEN"),
    queryFn: () => getInquiries({ page: 1, limit: 5, status: "OPEN" }),
  });

  if (isStatsLoading) {
    return (
      <TitleContainer title="대시보드" description="실시간 통계 현황">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </TitleContainer>
    );
  }

  const attendanceRate = stats
    ? Math.round((stats.activeAttendanceToday / Math.max(stats.totalUsers, 1)) * 100)
    : 0;

  const pendingDutyRequestsList = dutyRequestsData?.list || [];
  const pendingInquiriesList = inquiriesData?.list || [];

  return (
    <TitleContainer
      title={
        <Group justify="space-between" align="center" style={{ width: "100%" }}>
          <div>
            <Title order={3}>서비스 운영 대시보드</Title>
            <Text size="xs" c="dimmed">
              전체 서비스 공통 지표와 서비스별 세부 지표를 확인합니다.
            </Text>
          </div>
          <Tooltip label="새로고침">
            <ActionIcon
              variant="default"
              size="input-xs"
              radius="md"
              onClick={() => refetchStats()}
              loading={isStatsFetching}
            >
              <RefreshCw size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* 1. 전체 서비스 공통 지표 */}
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={4}>공통 지표</Title>
            <Text size="xs" c="dimmed">
              {dayjs().format("YYYY년 M월 D일")} 기준
            </Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <StatCard
              title="전체 가입자"
              value={stats?.totalUsers || 0}
              unit="명"
              icon={<Users size={22} />}
              color="blue"
              subtitle={
                <Text size="xs" c="dimmed">
                  전체 서비스 누적 가입자
                </Text>
              }
            />
            <StatCard
              title="오늘 신규 가입"
              value={stats?.todaysStats.newUsers || 0}
              unit="명"
              icon={<TrendingUp size={22} />}
              color="teal"
              subtitle={
                <Text size="xs" c="dimmed">
                  오늘 0시(KST) 이후 가입
                </Text>
              }
            />
            <StatCard
              title="미답변 문의"
              value={stats?.pendingInquiries || 0}
              unit="건"
              icon={<MessageSquare size={22} />}
              color="red"
              subtitle={
                <Button
                  component={Link}
                  to="/inquiries"
                  variant="subtle"
                  color="red"
                  size="compact-xs"
                  rightSection={<ArrowRight size={12} />}
                >
                  문의 처리하기
                </Button>
              }
            />
            <StatCard
              title="오늘 답변 완료"
              value={stats?.todaysStats.resolvedInquiries || 0}
              unit="건"
              icon={<CheckCircle2 size={22} />}
              color="violet"
              subtitle={
                <Text size="xs" c="dimmed">
                  오늘 처리된 문의
                </Text>
              }
            />
          </SimpleGrid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <ThemeIcon color="red" variant="light" size="md" radius="md">
                  <MessageSquare size={18} />
                </ThemeIcon>
                <Title order={4}>미답변 문의사항</Title>
                <Badge size="md" color="red" variant="filled">
                  {stats?.pendingInquiries || 0}건 대기
                </Badge>
              </Group>
              <Button
                component={Link}
                to="/inquiries"
                variant="subtle"
                color="blue"
                size="xs"
                rightSection={<ArrowRight size={13} />}
              >
                전체 보기
              </Button>
            </Group>

            {isInquiriesLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : pendingInquiriesList.length > 0 ? (
              <Stack gap="xs">
                {pendingInquiriesList.map((inq) => (
                  <Paper
                    key={inq.id}
                    p="sm"
                    radius="md"
                    withBorder
                    style={{
                      cursor: "pointer",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                    }}
                    onClick={() => navigate(`/inquiries/${inq.id}`)}
                  >
                    <Group justify="space-between" align="center">
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs">
                          <Badge size="xs" color="red" variant="light">
                            답변대기
                          </Badge>
                          <Text size="sm" fw={700} truncate>
                            {inq.title}
                          </Text>
                        </Group>
                        <Text size="xs" c="dimmed">
                          작성자: {inq.user?.name || inq.user?.email || "사용자"} ·{" "}
                          {dayjs(inq.createdAt).format("YYYY.MM.DD HH:mm")}
                        </Text>
                      </Stack>

                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          {dayjs(inq.createdAt).fromNow()}
                        </Text>
                        <ChevronRight size={16} color="gray" />
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper p="xl" radius="md" withBorder bg="gray.0" style={{ textAlign: "center" }}>
                <CheckCircle2
                  size={28}
                  color="var(--mantine-color-teal-6)"
                  style={{ margin: "0 auto 8px" }}
                />
                <Text size="sm" fw={600} c="dimmed">
                  현재 미답변 문의사항이 없습니다.
                </Text>
                <Text size="xs" c="dimmed">
                  모든 문의에 답변이 완료되었습니다.
                </Text>
              </Paper>
            )}
          </Card>
        </Stack>

        {/* 2. 서비스별 세부 지표 */}
        <Tabs value={activeTab} onChange={handleTabChange} keepMounted={false}>
          <Tabs.List mb="md">
            <Tabs.Tab value="hospital" leftSection={<Building2 size={16} />}>
              병원 서비스 (대시보드·앱)
            </Tabs.Tab>
            <Tabs.Tab value="mini" leftSection={<Sparkles size={16} />}>
              DutyOn Mini
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="hospital">
            <Stack gap="lg">
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <StatCard
                  title="활성 병원"
                  value={stats?.totalCompanies || 0}
                  unit="곳"
                  icon={<Building2 size={22} />}
                  color="teal"
                  subtitle={
                    <Text size="xs" c="dimmed">
                      팀 {stats?.totalTeams || 0}개 · 근무 유형 {stats?.totalWorkShifts || 0}개
                    </Text>
                  }
                />
                <StatCard
                  title="오늘 활성 출근"
                  value={stats?.activeAttendanceToday || 0}
                  unit="명"
                  icon={<UserCheck size={22} />}
                  color="indigo"
                  subtitle={
                    <Badge color="indigo" variant="light" size="sm">
                      전체 가입자 대비 {attendanceRate}%
                    </Badge>
                  }
                />
                <StatCard
                  title="이번 달 근무 배정"
                  value={stats?.totalDutiesThisMonth || 0}
                  unit="개"
                  icon={<Calendar size={22} />}
                  color="orange"
                  subtitle={
                    <Text size="xs" c="dimmed">
                      이번 달 편성된 듀티 슬롯
                    </Text>
                  }
                />
                <StatCard
                  title="대기 근무 요청"
                  value={stats?.pendingDutyRequests || 0}
                  unit="건"
                  icon={<FileText size={22} />}
                  color="yellow"
                  subtitle={
                    <Badge
                      color="teal"
                      variant="light"
                      size="sm"
                      leftSection={<TrendingUp size={12} />}
                    >
                      오늘 신규 {stats?.todaysStats.newDutyRequests || 0}건
                    </Badge>
                  }
                />
              </SimpleGrid>

              <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <Group justify="space-between" mb="md">
                      <Group gap="xs">
                        <ThemeIcon color="yellow" variant="light" size="md" radius="md">
                          <ClipboardList size={18} />
                        </ThemeIcon>
                        <Title order={4}>대기 중인 근무 요청</Title>
                        <Badge size="md" color="yellow" variant="filled">
                          {stats?.pendingDutyRequests || 0}건 대기
                        </Badge>
                      </Group>
                      <Button
                        component={Link}
                        to="/duty-requests"
                        variant="subtle"
                        color="blue"
                        size="xs"
                        rightSection={<ArrowRight size={13} />}
                      >
                        전체 보기
                      </Button>
                    </Group>

                    {isDutyRequestsLoading ? (
                      <Group justify="center" py="md">
                        <Loader size="sm" />
                      </Group>
                    ) : pendingDutyRequestsList.length > 0 ? (
                      <Stack gap="xs">
                        {pendingDutyRequestsList.map((req) => (
                          <Paper
                            key={req.id}
                            p="sm"
                            radius="md"
                            withBorder
                            style={{
                              cursor: "pointer",
                              transition: "border-color 0.15s ease, background-color 0.15s ease",
                            }}
                            onClick={() => navigate(`/duty-requests/${req.id}`)}
                          >
                            <Group justify="space-between" align="center">
                              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                <Group gap="xs">
                                  {req.team?.company?.name && (
                                    <Badge size="xs" variant="light" color="blue">
                                      {req.team.company.name}
                                    </Badge>
                                  )}
                                  <Text size="sm" fw={700} truncate>
                                    {req.nurse?.user?.name || "간호사"} ({req.team?.name || "부서"})
                                  </Text>
                                </Group>
                                <Text size="xs" c="dimmed">
                                  근무 기간: {dayjs(req.startDate).format("YYYY.MM.DD")} ~{" "}
                                  {dayjs(req.endDate).format("YYYY.MM.DD")}
                                </Text>
                                {req.memo && (
                                  <Text size="xs" c="dark.3" lineClamp={1}>
                                    메모: {req.memo}
                                  </Text>
                                )}
                              </Stack>

                              <Group gap="xs">
                                <Text size="xs" c="dimmed">
                                  {dayjs(req.createdAt).fromNow()}
                                </Text>
                                <ChevronRight size={16} color="gray" />
                              </Group>
                            </Group>
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Paper
                        p="xl"
                        radius="md"
                        withBorder
                        bg="gray.0"
                        style={{ textAlign: "center" }}
                      >
                        <CheckCircle2
                          size={28}
                          color="var(--mantine-color-teal-6)"
                          style={{ margin: "0 auto 8px" }}
                        />
                        <Text size="sm" fw={600} c="dimmed">
                          현재 대기 중인 근무 요청이 없습니다.
                        </Text>
                        <Text size="xs" c="dimmed">
                          모든 요청이 완료되었습니다.
                        </Text>
                      </Paper>
                    )}
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
                    <Title order={4} mb="sm">
                      통계 분석 바로가기
                    </Title>
                    <Stack gap="xs">
                      <Paper
                        component={Link}
                        to="/statistics/user-trends"
                        p="xs"
                        radius="md"
                        withBorder
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          textDecoration: "none",
                          transition: "background-color 0.15s ease",
                        }}
                        className="hover-card"
                      >
                        <Group gap="xs">
                          <ThemeIcon color="teal" variant="light" size="md" radius="md">
                            <Users size={16} />
                          </ThemeIcon>
                          <div>
                            <Text size="xs" fw={700} c="dark">
                              사용자 성장·활동 통계
                            </Text>
                            <Text size="9px" c="dimmed">
                              가입자 추이 및 출퇴근/원티드 활동 분석
                            </Text>
                          </div>
                        </Group>
                        <ChevronRight size={14} color="gray" />
                      </Paper>

                      <Paper
                        component={Link}
                        to="/statistics/duty-requests"
                        p="xs"
                        radius="md"
                        withBorder
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          textDecoration: "none",
                        }}
                      >
                        <Group gap="xs">
                          <ThemeIcon color="indigo" variant="light" size="md" radius="md">
                            <ClipboardCheck size={16} />
                          </ThemeIcon>
                          <div>
                            <Text size="xs" fw={700} c="dark">
                              Duty 요청·응답률 통계
                            </Text>
                            <Text size="9px" c="dimmed">
                              병원별 근무표 요청 건수 및 응답률 분석
                            </Text>
                          </div>
                        </Group>
                        <ChevronRight size={14} color="gray" />
                      </Paper>

                      <Paper
                        component={Link}
                        to="/statistics/duty-generation"
                        p="xs"
                        radius="md"
                        withBorder
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          textDecoration: "none",
                        }}
                      >
                        <Group gap="xs">
                          <ThemeIcon color="blue" variant="light" size="md" radius="md">
                            <Sparkles size={16} />
                          </ThemeIcon>
                          <div>
                            <Text size="xs" fw={700} c="dark">
                              Duty 자동 생성 통계
                            </Text>
                            <Text size="9px" c="dimmed">
                              서버 기록 기반 AI 근무표 자동 생성 현황
                            </Text>
                          </div>
                        </Group>
                        <ChevronRight size={14} color="gray" />
                      </Paper>
                    </Stack>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="mini">
            <Stack gap="lg">
              <AnalyticsReportLinks />
              <Card withBorder radius="md" padding="lg">
                <Group justify="space-between" wrap="wrap" gap="sm">
                  <div>
                    <Title order={4}>내부 Duty 자동 생성 통계</Title>
                    <Text size="sm" c="dimmed">
                      GA 수치와 별개로 서버 기록 기반의 정확한 생성 건수를 확인합니다.
                    </Text>
                  </div>
                  <Button
                    component={Link}
                    to="/statistics/duty-generation"
                    variant="light"
                    rightSection={<ArrowRight size={16} />}
                  >
                    생성 통계 보기
                  </Button>
                </Group>
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </TitleContainer>
  );
}
